import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isSupervisorOrAbove, isAdminOrAbove, isInstructor } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ChalkboardTeacher, Trash, ArrowLeft, ClipboardText, Users as UsersIcon, PencilSimple } from "@phosphor-icons/react";

/* ============================================================
 * Training module
 *
 *  /training              → Classes tab (list + Record Attendance button)
 *  /training              → Sessions tab (past attendance records)
 *  /training              → Instructors tab (member ↔ authorized classes)
 *
 * Only supervisors and above see the "Record" / "Add Instructor" actions.
 * ========================================================== */

const MAX_ATTENDEES = 20;
const INITIAL_ATTENDEES = 5;

function etTodayYMD() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}
function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d + "T12:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return d; }
}
function firstNameKey(u) { return String(u.name || "").trim().split(/\s+/)[0].toLowerCase(); }

/* ---------- Type-to-search member picker (one per attendee row) ---------- */
const memberLabel = (m) => [m.name, (m.certifications || [m.certification]).filter(Boolean).join(", ")].filter(Boolean).join(" · ");

function MemberSearch({ index, members, value, takenIds, onPick, autoFocus }) {
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const inputRef = useRef(null);
  useEffect(() => { if (autoFocus && !value) inputRef.current?.focus(); }, [autoFocus, value]);
  const selected = value ? members.find((m) => m.id === value) : null;
  const matches = useMemo(() => {
    const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return [];
    return members.filter((m) => {
      if (takenIds.has(m.id)) return false;
      const hay = `${m.name || ""} ${m.email || ""} ${m.username || ""}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    }).slice(0, 8);
  }, [q, members, takenIds]);

  if (selected) {
    return (
      <div className="flex-1 min-w-0 flex items-center justify-between border border-emerald-300 bg-emerald-50 rounded-sm h-9 px-2 text-sm" data-testid={`tr-member-${index}-selected`}>
        <span className="truncate">{memberLabel(selected)}</span>
        <button type="button" onClick={() => { onPick(index, ""); setQ(""); }} className="text-slate-500 hover:text-slate-900 text-xs ml-2 shrink-0" data-testid={`tr-member-${index}-clear`}>Change</button>
      </div>
    );
  }
  function pick(m, viaEnter) { onPick(index, m.id, viaEnter); setQ(""); setHi(0); }
  function onKeyDown(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (matches[hi]) pick(matches[hi], true); }
    else if (e.key === "Escape" && q) { e.stopPropagation(); setQ(""); }
  }
  return (
    <div className="flex-1 min-w-0">
      <Input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setHi(0); }} onKeyDown={onKeyDown}
        placeholder="Type a name, email or username…" className="rounded-sm h-9 text-sm" data-testid={`tr-member-${index}`} autoComplete="off" />
      {q.trim() && (
        <div className="mt-1 border border-slate-200 rounded-sm bg-white shadow-sm max-h-56 overflow-y-auto" data-testid={`tr-member-${index}-results`}>
          {matches.length === 0 && <div className="px-2 py-2 text-xs text-slate-500 italic">No matching members (already-added members are hidden)</div>}
          {matches.map((m, j) => (
            <button type="button" key={m.id} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(m, false)} onMouseEnter={() => setHi(j)}
              className={`w-full text-left px-2 py-1.5 text-sm truncate ${j === hi ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`} data-testid={`tr-member-${index}-option-${m.id}`}>
              {memberLabel(m)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Helper: Attendance dialog ---------- */
function AttendanceDialog({ open, onClose, cls, users, onSaved, session }) {
  const [date, setDate] = useState(etTodayYMD());
  const [instructorId, setInstructorId] = useState("");
  const [slots, setSlots] = useState(Array(INITIAL_ATTENDEES).fill(""));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [forceOverride, setForceOverride] = useState(false);

  // Reset the form every time the dialog opens for a different class.
  useEffect(() => {
    if (open) {
      setDate(session?.session_date || etTodayYMD());
      setInstructorId(session?.instructor_user_id || "");
      setSlots(session ? [...session.member_ids, ""] : Array(INITIAL_ATTENDEES).fill(""));
      setFocusIdx(session ? session.member_ids.length : 0);
      setNotes(session?.notes || "");
      setForceOverride(false);
    }
  }, [open, cls?.id, session]);

  // Instructor list = anyone authorized to teach this class. Falls back to
  // all supervisors/admins if no dedicated instructor is on file yet so the
  // form is never blocked.
  const instructors = useMemo(() => {
    if (!cls) return [];
    const authorized = users.filter((u) => (u.instructor_classes || []).includes(cls.id));
    const list = authorized.length > 0 ? authorized : users.filter((u) => isSupervisorOrAbove(u) || isAdminOrAbove(u));
    // Keep the session's current instructor selectable even if no longer authorized.
    const cur = session?.instructor_user_id && !list.some((u) => u.id === session.instructor_user_id) && users.find((u) => u.id === session.instructor_user_id);
    return cur ? [cur, ...list] : list;
  }, [users, cls, session]);

  // Roster = every active member (EMT and non-EMT personnel alike).
  const eligibleMembers = useMemo(() => users.filter((u) => u.active !== false || session?.member_ids?.includes(u.id))
    .sort((a, b) => firstNameKey(a).localeCompare(firstNameKey(b))), [users, session]);

  const [focusIdx, setFocusIdx] = useState(0);
  // Enter on a match fills the row and moves to the next empty row (adding one if needed).
  function setSlot(i, id, viaEnter) {
    const a = [...slots]; a[i] = id;
    if (viaEnter && id) {
      let next = a.findIndex((v, j) => j > i && !v);
      if (next === -1 && a.length < MAX_ATTENDEES) { a.push(""); next = a.length - 1; }
      if (next !== -1) setFocusIdx(next);
    }
    setSlots(a);
  }
  function addSlot() { if (slots.length < MAX_ATTENDEES) setSlots([...slots, ""]); }
  function removeSlot(i) { if (slots.length > 1) setSlots(slots.filter((_, j) => j !== i)); }

  async function submit(e) {
    e.preventDefault();
    if (!cls) return;
    const memberIds = Array.from(new Set(slots.filter(Boolean)));
    if (memberIds.length === 0) { toast.error("Select at least one member"); return; }
    setSaving(true);
    try {
      const payload = { session_date: date, instructor_user_id: instructorId || null, member_ids: memberIds, notes: notes || null, force: forceOverride };
      if (session) await api.patch(`/training/sessions/${session.id}`, payload);
      else await api.post("/training/sessions", { class_id: cls.id, ...payload });
      toast.success(session ? "Attendance updated" : `Recorded ${memberIds.length} attendance entr${memberIds.length === 1 ? "y" : "ies"}`);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed";
      // 409 → duplicate guard triggered. Show a targeted prompt so the
      // admin can decide whether to override rather than silently fail.
      if (err.response?.status === 409 && !forceOverride) {
        toast.error(detail + " · Toggle 'Override duplicates' to save anyway.");
        setForceOverride(true);
      } else {
        toast.error(detail);
      }
    } finally { setSaving(false); }
  }

  if (!cls) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b border-slate-200 pb-2">
          <DialogTitle className="font-display text-xl">{session ? "Edit Attendance" : "Record Attendance"}</DialogTitle>
          <div className="text-xs text-slate-500 mt-1">Class: <span className="font-semibold text-slate-800">{cls.name}</span></div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>*]:min-w-0">
            <div className="space-y-1.5">
              <Label className="tab-tag">Training Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required className="rounded-sm" data-testid="tr-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="tab-tag">Instructor</Label>
              <select
                value={instructorId}
                onChange={(e) => setInstructorId(e.target.value)}
                className="w-full min-w-0 border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white"
                data-testid="tr-instructor"
              >
                <option value="">— (unspecified) —</option>
                {instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
              <div className="text-[10px] text-slate-500">Authorized instructors first; falls back to supervisors.</div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="tab-tag">Attendees ({slots.filter(Boolean).length} / {MAX_ATTENDEES})</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-sm h-7" onClick={addSlot} disabled={slots.length >= MAX_ATTENDEES} data-testid="tr-addrow">
                <Plus size={12} weight="bold" className="mr-1" /> Add Attendee
              </Button>
            </div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {slots.map((val, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="mono text-[10px] text-slate-400 w-6 text-right pt-3">{i + 1}.</span>
                  <MemberSearch index={i} members={eligibleMembers} value={val} onPick={setSlot} autoFocus={focusIdx === i}
                    takenIds={new Set(slots.filter((v, j) => v && j !== i))} />
                  <Button type="button" size="icon" variant="ghost" className="text-red-600 h-8 w-8" onClick={() => removeSlot(i)} data-testid={`tr-del-${i}`}>
                    <Trash size={13} />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="tab-tag">Notes (optional)</Label>
            <Textarea className="rounded-sm min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="tr-notes" />
          </div>

          {forceOverride && (
            <label className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-300 rounded-sm px-2 py-1.5" data-testid="tr-force">
              <Checkbox checked={forceOverride} onCheckedChange={(v) => setForceOverride(!!v)} />
              <span>Override duplicate protection — allow re-recording this class/date for members already listed.</span>
            </label>
          )}

          <DialogFooter className="pt-3 border-t border-slate-200">
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm bg-slate-900 text-white" data-testid="tr-save">
              {saving ? "Saving…" : session ? "Save Changes" : "Submit Attendance"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Instructor add/edit ---------- */
function InstructorDialog({ open, onClose, editing, classes, users, onSaved }) {
  const [memberId, setMemberId] = useState("");
  const [classIds, setClassIds] = useState([]);

  useEffect(() => {
    if (open) {
      setMemberId(editing?.id || "");
      setClassIds(editing?.instructor_classes || []);
    }
  }, [open, editing]);

  function toggle(cid) {
    setClassIds((cur) => cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid]);
  }

  async function save(e) {
    e.preventDefault();
    if (!memberId) { toast.error("Select a member"); return; }
    try {
      await api.patch(`/training/instructors/${memberId}`, { class_ids: classIds });
      toast.success("Instructor saved");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  const sortedUsers = [...users].sort((a, b) => firstNameKey(a).localeCompare(firstNameKey(b)));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit Instructor" : "Add Instructor"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="tab-tag">Member</Label>
            <select
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              disabled={!!editing}
              className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white disabled:bg-slate-100"
              data-testid="ins-member"
            >
              <option value="">— select —</option>
              {sortedUsers.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label className="tab-tag">Authorized to Teach</Label>
            <div className="grid grid-cols-1 gap-1 max-h-64 overflow-y-auto border border-slate-200 rounded-sm p-2 bg-slate-50">
              {classes.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-sm px-2 py-1.5 hover:border-slate-400 cursor-pointer" data-testid={`ins-cls-${c.id}`}>
                  <Checkbox checked={classIds.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
                  <span>{c.name}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="ins-save">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Class Settings dialog (Add / Rename / Deactivate) ---------- */
function ClassSettingsDialog({ open, onClose, classes, onSaved }) {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (open) setItems(classes.map((c) => ({ ...c })));
  }, [open, classes]);

  async function addClass() {
    if (!newName.trim()) return;
    try {
      await api.post("/training/classes", { name: newName.trim() });
      setNewName("");
      onSaved && onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }
  async function rename(c, name) {
    try { await api.patch(`/training/classes/${c.id}`, { name }); onSaved && onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }
  async function toggleActive(c) {
    if (c.active) {
      if (!window.confirm(`Deactivate "${c.name}"? Historical records will be preserved.`)) return;
      try { await api.delete(`/training/classes/${c.id}`); onSaved && onSaved(); }
      catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    } else {
      try { await api.patch(`/training/classes/${c.id}`, { active: true }); onSaved && onSaved(); }
      catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Training Classes</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="flex gap-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New class name" className="rounded-sm" data-testid="cls-new-name" />
            <Button type="button" onClick={addClass} className="rounded-sm bg-slate-900 text-white" data-testid="cls-add">
              <Plus size={12} weight="bold" className="mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto border border-slate-200 rounded-sm p-2">
            {items.map((c) => (
              <div key={c.id} className="flex items-center gap-2" data-testid={`cls-row-${c.id}`}>
                <Input
                  defaultValue={c.name}
                  onBlur={(e) => { if (e.target.value.trim() && e.target.value !== c.name) rename(c, e.target.value.trim()); }}
                  className={`rounded-sm flex-1 ${c.active ? "" : "opacity-50 line-through"}`}
                />
                <Button type="button" variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => toggleActive(c)}>
                  {c.active ? "Deactivate" : "Reactivate"}
                </Button>
              </div>
            ))}
            {items.length === 0 && <div className="text-xs text-slate-500 p-2">No classes yet.</div>}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose} className="rounded-sm bg-slate-900 text-white">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ Main page ============ */
export default function Training() {
  const { user } = useAuth();
  // Class + instructor management is admin-tier (matches backend); attendance is
  // supervisors/admins for any class, instructors for their authorized classes.
  const canManage = isAdminOrAbove(user);
  const canRecord = (cls) => isSupervisorOrAbove(user) || isAdminOrAbove(user) || isInstructor(user, cls.id);

  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [instructors, setInstructors] = useState([]);

  const [attendClass, setAttendClass] = useState(null);
  const [editSession, setEditSession] = useState(null);
  const canEditSession = (s) => isSupervisorOrAbove(user) || isAdminOrAbove(user) || [s.instructor_user_id, s.created_by].includes(user?.id);
  const [instructorOpen, setInstructorOpen] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  async function loadAll() {
    const [c, s, u, i] = await Promise.all([
      api.get("/training/classes?include_inactive=true"),
      api.get("/training/sessions"),
      api.get("/users/"),
      api.get("/training/instructors"),
    ]);
    setClasses(c.data);
    setSessions(s.data);
    setUsers(u.data);
    setInstructors(i.data);
  }
  useEffect(() => { loadAll(); }, []);

  const activeClasses = classes.filter((c) => c.active !== false);
  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c])), [classes]);

  return (
    <AppLayout
      title="Training"
      subtitle="Group training sessions, member training history, and instructor authorizations"
      actions={
        canManage && (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setSettingsOpen(true)} data-testid="btn-cls-settings">
              <ChalkboardTeacher size={14} weight="bold" className="mr-1" /> Manage Classes
            </Button>
          </div>
        )
      }
    >
      <Tabs defaultValue="classes" className="w-full">
        <TabsList className="rounded-sm bg-slate-100 mb-4">
          <TabsTrigger value="classes" data-testid="tab-classes" className="rounded-sm">Classes</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions" className="rounded-sm">Sessions</TabsTrigger>
          <TabsTrigger value="instructors" data-testid="tab-instructors" className="rounded-sm">Instructors</TabsTrigger>
        </TabsList>

        {/* Classes tab */}
        <TabsContent value="classes">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {activeClasses.length === 0 && <div className="text-sm text-slate-500 p-4 border border-dashed border-slate-200 rounded-sm">No active classes.</div>}
            {activeClasses.map((c) => {
              const recent = sessions.find((s) => s.class_id === c.id);
              const authorized = instructors.filter((i) => (i.instructor_classes || []).includes(c.id));
              return (
                <div key={c.id} className="surface-grid p-4 flex flex-col gap-3" data-testid={`cls-card-${c.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display font-bold text-slate-900">{c.name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Last session: {recent ? `${fmtDate(recent.session_date)} · ${recent.member_ids.length} attendee${recent.member_ids.length === 1 ? "" : "s"}` : "—"}
                      </div>
                    </div>
                    {canRecord(c) && (
                      <Button size="sm" className="rounded-sm bg-slate-900 text-white text-xs" onClick={() => setAttendClass(c)} data-testid={`cls-record-${c.id}`}>
                        <ClipboardText size={12} weight="bold" className="mr-1" /> Record Attendance
                      </Button>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1 flex-wrap">
                    <UsersIcon size={12} weight="bold" />
                    {authorized.length === 0 ? <span className="italic">no authorized instructors</span> : authorized.map((i) => (
                      <Badge key={i.id} variant="outline" className="rounded-sm text-[10px]">{i.name}</Badge>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Sessions tab */}
        <TabsContent value="sessions">
          <div className="surface-grid">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Class</th>
                  <th className="px-4 py-2 text-left">Instructor</th>
                  <th className="px-4 py-2 text-left">Attendees</th>
                  <th className="px-4 py-2 text-left">Recorded by</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No sessions recorded yet.</td></tr>
                ) : sessions.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100" data-testid={`sess-row-${s.id}`}>
                    <td className="px-4 py-2 mono text-xs">{fmtDate(s.session_date)}</td>
                    <td className="px-4 py-2 font-medium">{s.class_name}</td>
                    <td className="px-4 py-2">{s.instructor_name || <span className="text-slate-400 italic">—</span>}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {s.member_names.map((n) => <Badge key={n} variant="outline" className="rounded-sm text-[10px]">{n}</Badge>)}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-[11px] text-slate-500">
                      {s.created_by_name}
                      {s.updated_by_name && <div className="text-[10px] text-slate-500" data-testid={`sess-edited-${s.id}`}>edited by {s.updated_by_name}</div>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {canEditSession(s) && (
                        <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => setEditSession(s)} data-testid={`sess-edit-${s.id}`}>
                          <PencilSimple size={12} weight="bold" className="mr-1" /> Edit
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Instructors tab */}
        <TabsContent value="instructors">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-500">Members authorized to teach classes. Click a name to open their profile.</p>
            {canManage && (
              <Button className="rounded-sm bg-slate-900 text-white" onClick={() => { setEditingInstructor(null); setInstructorOpen(true); }} data-testid="btn-add-instructor">
                <Plus size={12} weight="bold" className="mr-1" /> Add Instructor
              </Button>
            )}
          </div>
          <div className="surface-grid">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2 text-left">Member</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Authorized Classes</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {instructors.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No instructors yet.</td></tr>
                ) : instructors.map((ins) => (
                  <tr key={ins.id} className="border-b border-slate-100" data-testid={`ins-row-${ins.id}`}>
                    <td className="px-4 py-2 font-medium"><Link to={`/members/${ins.id}`} className="hover:text-blue-700 hover:underline">{ins.name}</Link></td>
                    <td className="px-4 py-2"><Badge variant="outline" className="rounded-sm text-[10px]">{ins.role}</Badge></td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(ins.instructor_classes || []).map((cid) => (
                          <Badge key={cid} className="rounded-sm bg-slate-900 text-white text-[10px]">{classById[cid]?.name || cid}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {canManage && (
                        <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => { setEditingInstructor(ins); setInstructorOpen(true); }} data-testid={`ins-edit-${ins.id}`}>
                          Edit
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <AttendanceDialog
        open={!!attendClass}
        onClose={() => setAttendClass(null)}
        cls={attendClass}
        users={users}
        onSaved={loadAll}
      />
      <AttendanceDialog
        open={!!editSession}
        onClose={() => setEditSession(null)}
        cls={editSession ? (classById[editSession.class_id] || { id: editSession.class_id, name: editSession.class_name }) : null}
        session={editSession}
        users={users}
        onSaved={loadAll}
      />
      <InstructorDialog
        open={instructorOpen}
        onClose={() => { setInstructorOpen(false); setEditingInstructor(null); }}
        editing={editingInstructor}
        classes={activeClasses}
        users={users}
        onSaved={loadAll}
      />
      <ClassSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        classes={classes}
        onSaved={loadAll}
      />
    </AppLayout>
  );
}
